import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ChevronLeft, ChevronRight, Calendar, CheckCircle2, ArrowRight } from 'lucide-react';
import type { EstadoOt, MonthlyTicketItem } from '@sime/shared';
import { ALL_STATES, STATE_COLORS } from '@sime/shared';
import { DayCell } from './day-cell';
import { MobileTicketRow } from './mobile-ticket-row';
import { StatusFilter } from './status-filter';
import { EquipoFilter } from '@/components/filters/equipo-filter';
import { SearchInput } from '@/components/ui/search-input';
import type { MonthlyFilters } from '@/api/plan-matriz-monthly';
import { useMonthlyTickets, useRescheduleTicket } from '@/api/plan-matriz-monthly';
import { useAmbitos } from '@/hooks/use-ambitos';
import { useAmbitoStore } from '@/stores/ambito.store';
import { ticketMatchesQuery } from '@/lib/ticket-search';
import { parseEstadosParam, serializeEstadosParam } from '@/lib/estado-filter-params';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const WEEKDAY_HEADERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function buildDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function todayDateStr(): string {
  const now = new Date();
  return buildDateStr(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
}

function buildCalendarGrid(year: number, month: number): Array<{ date: string; day: number; currentMonth: boolean }> {
  const periodStart = new Date(Date.UTC(year, month - 2, 21));
  const periodEnd   = new Date(Date.UTC(year, month - 1, 21));

  const startDow = (periodStart.getUTCDay() + 6) % 7;
  const gridStart = new Date(periodStart);
  gridStart.setUTCDate(gridStart.getUTCDate() - startDow);

  const cells: Array<{ date: string; day: number; currentMonth: boolean }> = [];
  const cursor = new Date(gridStart);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- intentional loop with break
  while (true) {
    const inPeriod = cursor >= periodStart && cursor < periodEnd;
    const d = cursor.getUTCDate();
    const m = cursor.getUTCMonth() + 1;
    const y = cursor.getUTCFullYear();
    cells.push({ date: buildDateStr(y, m, d), day: d, currentMonth: inPeriod });

    cursor.setUTCDate(cursor.getUTCDate() + 1);

    if (cursor >= periodEnd && cells.length % 7 === 0) {
      break;
    }
  }

  return cells;
}

/**
 * Returns whether a date string falls within the SIME period for the given year/month.
 * SIME periods: 21st of prev month (inclusive) to 21st of current month (exclusive).
 */
function parseDateStr(dateStr: string): [number, number, number] {
  const parts = dateStr.split('-');
  return [Number(parts[0]), Number(parts[1]), Number(parts[2])];
}

function isDateInPeriod(dateStr: string, year: number, month: number): boolean {
  const periodStart = new Date(Date.UTC(year, month - 2, 21));
  const periodEnd = new Date(Date.UTC(year, month - 1, 21));
  const [y, m, d] = parseDateStr(dateStr);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date >= periodStart && date < periodEnd;
}

/**
 * Returns the SIME period (year, month) that contains a given date string.
 * E.g. "2026-03-21" → { year: 2026, month: 4 } (April period: Mar 21 - Apr 20)
 */
function getPeriodForDate(dateStr: string): { year: number; month: number } {
  const [y, m, d] = parseDateStr(dateStr);
  // If day >= 21, the date belongs to the NEXT month's period
  if (d >= 21) {
    const nextMonth = m + 1;
    return nextMonth > 12 ? { year: y + 1, month: 1 } : { year: y, month: nextMonth };
  }
  return { year: y, month: m };
}

interface RescheduleResult {
  correlativo: string;
  newDate: string;
  isOutsidePeriod: boolean;
  targetPeriodYear: number;
  targetPeriodMonth: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MonthlyCalendarProps {
  initialYear?: number;
  initialMonth?: number;
}

export function MonthlyCalendar({ initialYear, initialMonth }: MonthlyCalendarProps) {
  const today = new Date();
  const defaultMonth = (() => {
    let m = today.getUTCMonth() + 1;
    if (today.getUTCDate() >= 21) {
      m += 1;
      if (m > 12) m = 1;
    }
    return m;
  })();
  const defaultYear = (() => {
    let y = today.getUTCFullYear();
    if (today.getUTCDate() >= 21 && today.getUTCMonth() + 1 === 12) {
      y += 1;
    }
    return y;
  })();

  const navigate = useNavigate();

  const handleOpenTicket = useCallback((id: number) => {
    void navigate(`/tickets/${id}`, {
      state: { from: window.location.pathname + window.location.search },
    });
  }, [navigate]);

  // Period + filters live in URL search params so they're (a) deep-linkable,
  // (b) preserved by the back-button via location.state.from, and (c) shared
  // when switching between the monthly/weekly/daily tabs.
  const [searchParams, setSearchParams] = useSearchParams();

  // Local helper renamed to avoid shadowing the global `parseInt`. Uses
  // `Number.parseInt(raw, 10)` so empty strings and non-numeric suffixes are
  // rejected (`Number('')` would silently succeed as 0).
  const getNumParam = (key: string): number | undefined => {
    const raw = searchParams.get(key);
    if (raw === null || raw === '') return undefined;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : undefined;
  };

  const yearFromUrl = getNumParam('year');
  const monthFromUrl = getNumParam('month');
  const year = (yearFromUrl !== undefined && yearFromUrl >= 2020 && yearFromUrl <= 2100)
    ? yearFromUrl
    : (initialYear ?? defaultYear);
  const month = (monthFromUrl !== undefined && monthFromUrl >= 1 && monthFromUrl <= 12)
    ? monthFromUrl
    : (initialMonth ?? defaultMonth);

  // Ambito filter has TWO sources: the URL (for deep-linkable / shareable
  // calendar views) and the global ambito store driven by the header dropdown
  // (consistent with KPI / Dashboard / Tickets-list pages, which only read the
  // store). URL takes precedence when present; otherwise we honor the header
  // selection so changing ambito globally also filters the monthly calendar
  // for admins. Without this fallback, an admin who picked "Baja Tension" in
  // the header would still see BT + INST tickets simultaneously here.
  const globalAmbitoId = useAmbitoStore((s) => s.selectedAmbitoId);
  const setGlobalAmbito = useAmbitoStore((s) => s.setSelectedAmbito);
  const urlAmbitoId = getNumParam('ambitoId');
  const ambitoId = urlAmbitoId ?? (globalAmbitoId ?? undefined);
  const subcontratoId = getNumParam('subcontratoId');
  // Equipment filter (tipo_equipo → catalogoeco) also lives in the URL, so it's
  // deep-linkable AND shared across the monthly/weekly/daily tabs (TicketsPage
  // only swaps the `tab` param).
  const tipoEquipoId = getNumParam('tipoEquipoId');
  const catalogoecoId = getNumParam('catalogoecoId');
  const filters: MonthlyFilters = useMemo(
    () => ({ ambitoId, subcontratoId, tipoEquipoId, catalogoecoId }),
    [ambitoId, subcontratoId, tipoEquipoId, catalogoecoId],
  );

  /**
   * Write one or more URL params, preserving the rest. `undefined` removes
   * the param entirely (cleaner URLs than `?ambitoId=`).
   */
  const updateParams = useCallback(
    (updates: Record<string, string | number | undefined>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (v === undefined || v === '') next.delete(k);
          else next.set(k, String(v));
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const setYearMonth = useCallback((newYear: number, newMonth: number) => {
    updateParams({ year: newYear, month: newMonth });
  }, [updateParams]);

  const setAmbitoId = useCallback((id: number | undefined) => {
    // Sync both URL (deep-link) and global store (header dropdown) so the
    // selection propagates to other pages and survives navigation. Clearing the
    // ECO filter too: a tipo/eco from the previous ambito no longer applies
    // (mirrors useEquipmentFilter's reset-on-ambito behavior).
    updateParams({ ambitoId: id, tipoEquipoId: undefined, catalogoecoId: undefined });
    setGlobalAmbito(id ?? null);
  }, [updateParams, setGlobalAmbito]);

  const setTipoEquipoId = useCallback((id: number | undefined) => {
    updateParams({ tipoEquipoId: id, catalogoecoId: undefined });
  }, [updateParams]);

  const setCatalogoEcoId = useCallback((id: number | undefined) => {
    updateParams({ catalogoecoId: id });
  }, [updateParams]);

  // Post-mount: when the header dropdown writes the global store, mirror
  // that into the URL so the existing `urlAmbitoId ?? globalAmbitoId`
  // precedence resolves to the fresh value instead of a stale URL param.
  // First render is skipped so deep-links (e.g. `?ambitoId=4` shared in
  // chat) win over whatever the store happened to hold at mount time.
  const isFirstStoreSyncRef = useRef(true);
  useEffect(() => {
    if (isFirstStoreSyncRef.current) {
      isFirstStoreSyncRef.current = false;
      return;
    }
    const storeAsUndefined = globalAmbitoId ?? undefined;
    if (storeAsUndefined === urlAmbitoId) return;
    // Header ambito change also clears the ECO filter (stale for the new ambito).
    updateParams({ ambitoId: storeAsUndefined, tipoEquipoId: undefined, catalogoecoId: undefined });
  }, [globalAmbitoId, urlAmbitoId, updateParams]);

  const setSubcontratoId = useCallback((id: number | undefined) => {
    updateParams({ subcontratoId: id });
  }, [updateParams]);

  // Status chips (`estados`) + free-text search (`q`) live in the URL, like the
  // ambito/fecha/equipo filters above, so they survive the open-ticket →
  // ejecutar → volver round-trip instead of resetting from local state.
  const activeFilters = useMemo(
    () => parseEstadosParam(searchParams.get('estados')),
    [searchParams],
  );
  const search = searchParams.get('q') ?? '';

  const toggleFilter = useCallback((estado: EstadoOt) => {
    const next = new Set(activeFilters);
    if (next.has(estado)) next.delete(estado); else next.add(estado);
    updateParams({ estados: serializeEstadosParam(next) });
  }, [activeFilters, updateParams]);

  const resetFilters = useCallback(() => {
    updateParams({ estados: undefined });
  }, [updateParams]);

  const setSearch = useCallback((value: string) => {
    updateParams({ q: value });
  }, [updateParams]);

  // Data
  const { data, isLoading, isError } = useMonthlyTickets(year, month, filters);
  const reschedule = useRescheduleTicket(year, month, filters);
  const { data: ambitos } = useAmbitos();

  const monthly = data?.data;
  const todayStr = todayDateStr();

  const calendarCells = useMemo(() => buildCalendarGrid(year, month), [year, month]);
  const weekCount = calendarCells.length / 7;

  // Navigation — writes to URL so prev/next/today produce shareable links and
  // round-trip through the ticket-detail back button cleanly.
  const goToPrev = useCallback(() => {
    if (month === 1) setYearMonth(year - 1, 12);
    else setYearMonth(year, month - 1);
  }, [year, month, setYearMonth]);

  const goToNext = useCallback(() => {
    if (month === 12) setYearMonth(year + 1, 1);
    else setYearMonth(year, month + 1);
  }, [year, month, setYearMonth]);

  const goToToday = useCallback(() => {
    const now = new Date();
    let y = now.getUTCFullYear();
    let m = now.getUTCMonth() + 1;
    if (now.getUTCDate() >= 21) {
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    setYearMonth(y, m);
  }, [setYearMonth]);

  // Drag-drop
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [lastReschedule, setLastReschedule] = useState<RescheduleResult | null>(null);

  // Auto-dismiss success banner after 5 seconds
  useEffect(() => {
    if (!lastReschedule) return;
    const timer = setTimeout(() => { setLastReschedule(null); }, 5000);
    return () => { clearTimeout(timer); };
  }, [lastReschedule]);

  const handleDragStart = useCallback((ticketId: number, _estado: string) => {
    if (reschedule.isPending) return;
    setDraggingId(ticketId);
  }, [reschedule.isPending]);

  const handleDrop = useCallback((ticketId: number, targetDate: string) => {
    setDraggingId(null);
    if (reschedule.isPending) return;
    const existing = monthly?.days[targetDate]?.find((t) => t.id === ticketId);
    if (existing) return;

    // Find the ticket's correlativo for the success message
    let correlativo = '';
    if (monthly) {
      for (const tickets of Object.values(monthly.days)) {
        const found = tickets.find((t) => t.id === ticketId);
        if (found) { correlativo = found.correlativo; break; }
      }
    }

    const outsidePeriod = !isDateInPeriod(targetDate, year, month);
    const targetPeriod = outsidePeriod ? getPeriodForDate(targetDate) : { year, month };

    reschedule.mutate(
      { ticketId, newDate: targetDate },
      {
        onSuccess: () => {
          setLastReschedule({
            correlativo,
            newDate: targetDate,
            isOutsidePeriod: outsidePeriod,
            targetPeriodYear: targetPeriod.year,
            targetPeriodMonth: targetPeriod.month,
          });
        },
      },
    );
  }, [monthly, reschedule, year, month]);

  const goToPeriod = useCallback((targetYear: number, targetMonth: number) => {
    setYearMonth(targetYear, targetMonth);
    setLastReschedule(null);
  }, [setYearMonth]);

  const ticketsForDate = useCallback(
    (dateStr: string): MonthlyTicketItem[] => {
      const items = monthly?.days[dateStr] ?? [];
      // Status filtering happens inside DayCell; here we only apply search so
      // the grid day cells shrink to the matching tickets.
      if (!search.trim()) return items;
      return items.filter((t) => ticketMatchesQuery(t, search));
    },
    [monthly, search],
  );

  // Mobile list view
  const mobileTickets = useMemo(() => {
    if (!monthly) return [];
    return Object.entries(monthly.days)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([, tickets]) => tickets)
      .filter((t) => activeFilters.has(t.estadoOt) && ticketMatchesQuery(t, search));
  }, [monthly, activeFilters, search]);

  const prevMonthName = MONTH_NAMES[month === 1 ? 11 : month - 2] ?? '';

  return (
    <div className="flex flex-col h-full gap-1.5">
      {/* ── Toolbar: nav + filters + summary — single compact bar ── */}
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {/* Month navigation */}
        <button
          type="button"
          onClick={goToPrev}
          aria-label="Mes anterior"
          className="p-1.5 rounded-lg text-sime-text-secondary shadow-neu-xxs hover:shadow-neu-xs active:shadow-neu-inset-xs transition-all"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="text-center min-w-[180px]">
          <span className="text-sm font-bold text-sime-text-primary">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <span className="ml-1.5 text-[10px] text-sime-text-tertiary">
            21 {prevMonthName.slice(0, 3)} — 20 {(MONTH_NAMES[month - 1] ?? '').slice(0, 3)}
          </span>
        </div>

        <button
          type="button"
          onClick={goToNext}
          aria-label="Mes siguiente"
          className="p-1.5 rounded-lg text-sime-text-secondary shadow-neu-xxs hover:shadow-neu-xs active:shadow-neu-inset-xs transition-all"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={goToToday}
          className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-sime-text-secondary shadow-neu-xxs hover:shadow-neu-xs transition-all"
        >
          <Calendar className="h-3 w-3" />
          Hoy
        </button>

        <div className="h-5 w-px bg-sime-border-subtle/50 mx-1 hidden md:block" />

        {/* Filters */}
        <select
          value={ambitoId ?? ''}
          onChange={(e) => { setAmbitoId(e.target.value ? Number(e.target.value) : undefined); }}
          aria-label="Ambito"
          className="rounded-lg px-2 py-1 bg-transparent text-[11px] shadow-neu-inset-xs focus:ring-1 focus:ring-sime-accent focus:outline-none text-sime-text-primary"
        >
          <option value="">Todos ambitos</option>
          {(ambitos ?? []).map((a) => (
            <option key={a.id} value={a.id}>{a.nombre}</option>
          ))}
        </select>

        <select
          value={subcontratoId ?? ''}
          onChange={(e) => { setSubcontratoId(e.target.value ? Number(e.target.value) : undefined); }}
          aria-label="Subcontrato"
          className="rounded-lg px-2 py-1 bg-transparent text-[11px] shadow-neu-inset-xs focus:ring-1 focus:ring-sime-accent focus:outline-none text-sime-text-primary"
        >
          <option value="">Todos subcontratos</option>
        </select>

        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por nombre…"
          aria-label="Buscar tickets por nombre o correlativo"
          className="min-w-[150px] flex-1 md:max-w-[220px]"
        />

        <div className="h-5 w-px bg-sime-border-subtle/50 mx-1 hidden md:block" />

        {/* Summary inline */}
        {monthly ? (
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-sime-text-primary">{monthly.summary.total}</span>
            <span className="text-[10px] text-sime-text-tertiary">tickets</span>
            {ALL_STATES.map((estado: EstadoOt) => {
              const count = monthly.summary.byStatus[estado] ?? 0;
              if (count === 0) return null;
              const colors = STATE_COLORS[estado];
              return (
                <span
                  key={estado}
                  className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none"
                  style={{ backgroundColor: colors.bg, color: colors.text }}
                >
                  {count}
                </span>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* ── Equipment filter (tipo_equipo → catalogoeco) — hidden until an
          ambito is selected (EquipoFilter returns null otherwise) ── */}
      <div className="shrink-0">
        <EquipoFilter
          ambitoId={ambitoId}
          tipoEquipoId={tipoEquipoId}
          catalogoecoId={catalogoecoId}
          onTipoEquipoChange={setTipoEquipoId}
          onCatalogoEcoChange={setCatalogoEcoId}
        />
      </div>

      {/* ── Status filter buttons ── */}
      {monthly ? (
        <div className="shrink-0">
          <StatusFilter
            activeFilters={activeFilters}
            byStatus={monthly.summary.byStatus}
            onToggle={toggleFilter}
            onReset={resetFilters}
          />
        </div>
      ) : null}

      {/* ── Reschedule banners ── */}
      {reschedule.isError ? (
        <div role="alert" className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300">
          Error al reprogramar. Solo tickets Programado o Iniciado.
        </div>
      ) : null}
      {reschedule.isPending ? (
        <div className="shrink-0 flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
          <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          Reprogramando&hellip;
        </div>
      ) : null}
      {lastReschedule ? (
        <div className="shrink-0 flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs text-green-700 dark:border-green-800 dark:bg-green-950/50 dark:text-green-300">
          <CheckCircle2 className="h-3 w-3 shrink-0" />
          <span>
            {lastReschedule.correlativo} reprogramado al{' '}
            {lastReschedule.newDate.split('-').reverse().join('/')}
          </span>
          {lastReschedule.isOutsidePeriod ? (
            <button
              type="button"
              onClick={() => { goToPeriod(lastReschedule.targetPeriodYear, lastReschedule.targetPeriodMonth); }}
              className="ml-1 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-medium text-green-800 bg-green-100 hover:bg-green-200 dark:text-green-200 dark:bg-green-900/50 dark:hover:bg-green-800/50 transition-colors"
            >
              Ir a {MONTH_NAMES[lastReschedule.targetPeriodMonth - 1]}
              <ArrowRight className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      ) : null}

      {/* ── Calendar grid — fills remaining viewport ── */}
      {isLoading ? (
        <div className="flex-1 min-h-0 hidden md:grid md:grid-cols-7 gap-1">
          {WEEKDAY_HEADERS.map((h, i) => (
            <div key={`${h}-${i}`} className="py-1 text-center text-[10px] font-semibold text-sime-text-tertiary uppercase">{h}</div>
          ))}
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-lg bg-sime-surface-raised" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sime-text-secondary text-sm">Error al cargar. Intente nuevamente.</p>
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div
            className="hidden md:flex md:flex-col flex-1 min-h-0"
            role="grid"
            aria-label={`Periodo ${MONTH_NAMES[month - 1]} ${year}`}
            onDragEnd={() => { setDraggingId(null); }}
          >
            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-1 shrink-0" role="row">
              {WEEKDAY_HEADERS.map((h, i) => (
                <div
                  key={`${h}-${i}`}
                  role="columnheader"
                  className="py-1 text-center text-[10px] font-bold tracking-wider text-sime-text-tertiary uppercase"
                >
                  {h}
                </div>
              ))}
            </div>

            {/* Calendar cells — fill available height */}
            <div
              className="grid grid-cols-7 gap-1 flex-1 min-h-0"
              style={{ gridTemplateRows: `repeat(${weekCount}, 1fr)` }}
            >
              {calendarCells.map(({ date, day, currentMonth }) => (
                <DayCell
                  key={date}
                  date={date}
                  dayNumber={day}
                  tickets={ticketsForDate(date)}
                  isToday={date === todayStr}
                  isCurrentMonth={currentMonth}
                  activeFilters={activeFilters}
                  isRescheduling={reschedule.isPending}
                  onDrop={handleDrop}
                  onDragStart={handleDragStart}
                />
              ))}
            </div>

            {draggingId !== null ? (
              <p className="shrink-0 py-1 text-center text-[10px] text-sime-text-tertiary">
                Suelte en una celda para reprogramar
              </p>
            ) : null}
          </div>

          {/* Mobile list — clickable rows with date pill + Ejecutar CTA for
              executable states. Replaces the previous read-only divs that
              had no onClick handler. */}
          <div className="md:hidden flex-1 min-h-0 overflow-y-auto space-y-1.5 pb-4">
            {mobileTickets.length === 0 ? (
              <div className="py-12 text-center text-sime-text-tertiary text-sm">
                Sin tickets para este mes.
              </div>
            ) : (
              mobileTickets.map((ticket) => (
                <MobileTicketRow
                  key={ticket.id}
                  ticket={ticket}
                  onOpen={handleOpenTicket}
                  showDate
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
